import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Accordion, { Content, Header } from '@talkdesk/cobalt-react-accordion'
import Avatar from '@talkdesk/cobalt-react-avatar'
import Button from '@talkdesk/cobalt-react-button'
import Card from '@talkdesk/cobalt-react-card'
import Divider from '@talkdesk/cobalt-react-divider'
import Flex from '@talkdesk/cobalt-react-flex'
import Image from '@talkdesk/cobalt-react-image'
import Input from '@talkdesk/cobalt-react-input'
import { Heading, Text } from '@talkdesk/cobalt-react-typography'
import { Icon } from '@talkdesk/cobalt-react-icon'
import { useTheme } from '@talkdesk/cobalt-react-theme-provider'
import { TALKDESK_ICON } from '../../constants/images'
import { OAUTH_AUTHORIZE } from '../../constants/links'

const EMPTY_SCOPES = []
const ICON_SIZE = 36
const READ_AND_WRITE = 'readAndWrite'

const DecisionForm = ({
  csrf,
  clientId,
  state,
  scopes = EMPTY_SCOPES,
  children,
}) => (
  <form method='post' action={OAUTH_AUTHORIZE}>
    <input type='hidden' name={csrf.parameterName} value={csrf.token} />
    <input type='hidden' name='client_id' value={clientId} />
    <input type='hidden' name='state' value={state} />
    {scopes.map(({ slug }) => (
      <input key={slug} type='hidden' name='scope' value={slug} />
    ))}
    {children}
  </form>
)

const Consent = ({
  clientId,
  clientName,
  state,
  accountName,
  region,
  returnToHost,
  scopes = EMPTY_SCOPES,
  csrf,
  brandingSettings: { branding },
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [scopesOpen, setScopesOpen] = useState(false)
  const regionKey = `consent.region.${region}`
  const regionLabel = t(regionKey) === regionKey ? region : t(regionKey)

  return (
    <Flex direction='column' alignX='center' gap={5} paddingBottom={6}>
      <Flex alignY='center' gap={2}>
        <Avatar size='small' color='basic'>
          {clientName.charAt(0).toUpperCase()}
        </Avatar>
        <Icon name='swap_horiz' size='small' color={theme.gray600} />
        {branding.assets ? (
          <Image
            src={TALKDESK_ICON(branding.assets)}
            alt={t('common.logoAlt', { name: branding.name })}
            width={ICON_SIZE}
            height={ICON_SIZE}
          />
        ) : (
          <Icon name='talkdesk' size='medium' color={theme.gray600} />
        )}
      </Flex>

      <Flex direction='column' alignX='center' gap={2}>
        <Heading level={1} textAlign='center'>
          {t('consent.title', { clientName })}
        </Heading>
        <Text color={theme.gray600} textAlign='center'>
          {t('consent.subtitle', { clientName })}
        </Text>
      </Flex>

      <Card outlined>
        <Flex alignX='space-between' padding={3}>
          <Text color={theme.gray600}>{t('consent.account')}</Text>
          <Text>{accountName}</Text>
        </Flex>
        <Divider />
        <Flex alignX='space-between' padding={3}>
          <Text color={theme.gray600}>{t('consent.region')}</Text>
          <Text>{regionLabel}</Text>
        </Flex>
      </Card>

      <Flex direction='column' gap={3} width='100%'>
        <Heading level={4}>{t('consent.accessProfile')}</Heading>
        <Card outlined>
          <Flex
            gap={2}
            padding={3}
            role='radiogroup'
            aria-label={t('consent.accessProfile')}
          >
            <Input
              type='radio'
              id={READ_AND_WRITE}
              size='small'
              checked
              disabled
              readOnly
            />
            <Flex direction='column'>
              <label htmlFor={READ_AND_WRITE}>
                <Text inline>{t('consent.readAndWrite')}</Text>
              </label>
              <Text inline size='small' color={theme.gray600}>
                {t('consent.readAndWriteDescription', { clientName })}
              </Text>
            </Flex>
          </Flex>
        </Card>
      </Flex>

      <Flex direction='column' gap={3} width='100%'>
        <Heading level={4}>{t('consent.scopes')}</Heading>
        <Accordion open={scopesOpen}>
          <Header
            type='button'
            aria-expanded={scopesOpen}
            onClick={() => setScopesOpen(!scopesOpen)}
          >
            <Flex alignY='center' gap={2} padding={3}>
              <Icon
                name={scopesOpen ? 'expand_less' : 'expand_more'}
                size='small'
                aria-hidden
              />
              <Text inline>
                {t('consent.reviewScopes', { count: scopes.length })}
              </Text>
            </Flex>
          </Header>
          {scopesOpen && (
            <Content>
              <Flex direction='column' gap={3} padding={3}>
                {scopes.map(({ slug, description }) => (
                  <Flex key={slug} direction='column'>
                    <Text>{description}</Text>
                    <Text size='small' color={theme.gray600}>
                      {slug}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Content>
          )}
        </Accordion>
      </Flex>

      <Divider />

      <Flex direction='column' alignX='stretch' gap={3} width='100%'>
        <DecisionForm
          csrf={csrf}
          clientId={clientId}
          state={state}
          scopes={scopes}
        >
          <Button type='primary' htmlType='submit' shape='full-width'>
            {t('consent.allowAction')}
          </Button>
        </DecisionForm>
        <DecisionForm csrf={csrf} clientId={clientId} state={state}>
          <Button
            type='secondary'
            variation='outline'
            htmlType='submit'
            shape='full-width'
          >
            {t('consent.cancelAction')}
          </Button>
        </DecisionForm>
        {returnToHost && (
          <Text size='small' color={theme.gray600} textAlign='center'>
            {t('consent.returnTo', { host: returnToHost })}
          </Text>
        )}
      </Flex>
    </Flex>
  )
}

export default Consent
